import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { InviteUserForm } from 'src/components/Forms/InviteUserForm';
import { InviteList } from 'src/components/InviteList';
import { TeamTable } from 'src/components/TeamTable';
import { usePlanLimits } from 'src/hooks/usePlanLimits';
import palette from 'src/theme/palette';

// Colore emerald per il tema (stesso usato in Settings)
const emerald = '#10b981';

// Styled components per le tab personalizzate
const CustomTabs = styled(Tabs)(({ theme }) => ({
  '& .MuiTab-root': {
    color: palette.text.secondary,
    fontWeight: 500,
    textTransform: 'none',
    fontSize: '14px',
    minWidth: 'auto',
    padding: '12px 24px',
    '&.Mui-selected': {
      color: emerald,
      fontWeight: 600,
    },
  },
  '& .MuiTabs-indicator': {
    backgroundColor: emerald,
    height: '2px',
  },

  [theme.breakpoints.down('sm')]: {
    '& .MuiTab-root': {
      padding: '8px 12px',
      fontSize: '12px',
    },
  },
}));

const CustomTab = styled(Tab)(({ theme }) => ({
  '&.MuiTab-root': {
    color: palette.text.secondary,
    fontWeight: 500,
    textTransform: 'none',
    fontSize: '14px',
    minWidth: 'auto',
    padding: '12px 24px',
    '&.Mui-selected': {
      color: emerald,
      fontWeight: 600,
    },
  },

  [theme.breakpoints.down('sm')]: {
    '&.MuiTab-root': {
      padding: '8px 12px',
      fontSize: '12px',
    },
  },
}));

// Mantiene il contenuto fuori dalla sidebar senza usare 100vw, che taglia il lato destro.
const TeamContainer = styled(Box)(({ theme }) => ({
  backgroundColor: palette.background.default,
  minHeight: '100vh',
  padding: '24px',
  marginLeft: '240px',
  width: 'calc(100% - 240px)',
  maxWidth: 'calc(100% - 240px)',
  boxSizing: 'border-box',

  [theme.breakpoints.down('lg')]: {
    marginLeft: '220px',
    width: 'calc(100% - 220px)',
    maxWidth: 'calc(100% - 220px)',
  },

  [theme.breakpoints.down('md')]: {
    marginLeft: '200px',
    width: 'calc(100% - 200px)',
    maxWidth: 'calc(100% - 200px)',
    padding: '20px',
  },

  [theme.breakpoints.down('sm')]: {
    marginLeft: 0,
    width: '100%',
    padding: '16px',
    marginTop: '60px', // Spazio per header mobile
  },

  [theme.breakpoints.down('xs')]: {
    padding: '12px',
  },
}));

const TeamTitle = styled(Typography)(({ theme }) => ({
  color: emerald,
  fontWeight: 600,
  fontSize: '24px',
  marginBottom: '24px',

  [theme.breakpoints.down('md')]: {
    fontSize: '20px',
    marginBottom: '20px',
  },

  [theme.breakpoints.down('sm')]: {
    fontSize: '18px',
    marginBottom: '16px',
  },
}));

const TeamPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: palette.background.paper,
  borderRadius: '8px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  overflow: 'hidden',

  [theme.breakpoints.down('sm')]: {
    borderRadius: '6px',
  },
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`team-tabpanel-${index}`}
      aria-labelledby={`team-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
}

export const TeamManagement: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const planLimits = usePlanLimits();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleOpenAddMemberModal = () => {
    setIsAddMemberModalOpen(true);
  };

  const handleCloseAddMemberModal = () => {
    setIsAddMemberModalOpen(false);
  };

  return (
    <TeamContainer>
      <TeamTitle>Team</TeamTitle>

      <TeamPaper>
        <CustomTabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="team management tabs"
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : false}
        >
          <CustomTab label="TEAM" />
          <CustomTab label="INVITE USERS" />
          <CustomTab label="SENT INVITES" />
        </CustomTabs>

        <TabPanel value={tabValue} index={0}>
          <TeamTable onAddMember={handleOpenAddMemberModal} />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {!planLimits.canInviteUsers && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                bgcolor: '#fff3cd',
                border: '1px solid #ffeaa7',
                borderRadius: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: '#856404', fontWeight: 500 }}
              >
                ⚠️ Plan limit reached: The {planLimits.planName} plan allows
                maximum{' '}
                {planLimits.maxUsers === -1
                  ? 'unlimited users'
                  : `${planLimits.maxUsers} user${planLimits.maxUsers > 1 ? 's' : ''}`}
                .
                {planLimits.billingStatus !== 'active' &&
                  planLimits.billingStatus !== 'trial' &&
                  ' Upgrade your plan to invite more users.'}
              </Typography>
            </Box>
          )}
          <InviteUserForm disabled={!planLimits.canInviteUsers} />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <InviteList />
        </TabPanel>
      </TeamPaper>

      {/* Modal per aggiungere membro */}
      <Dialog
        open={isAddMemberModalOpen}
        onClose={handleCloseAddMemberModal}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            margin: { xs: '16px', sm: '32px' },
            width: { xs: 'calc(100% - 32px)', sm: 'auto' },
          },
        }}
      >
        <DialogTitle>Invite New Member</DialogTitle>
        <DialogContent>
          <InviteUserForm />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddMemberModal} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </TeamContainer>
  );
};
